import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/domains/organizations';
import { brandRepository } from '../repositories/brand.repository';
import { brandService } from '../services/brand.service';
import { supabaseBrandLogoStorage } from '../infrastructure/supabaseBrandLogoStorage';
import { brandKeys } from '../services/brand.keys';
import type { Brand, UpdateBrandData } from '../types/brand.types';

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async (
      data: UpdateBrandData & { logo_file?: File | null }
    ): Promise<Brand> => {
      if (!orgId) throw new Error('No organization selected');

      return brandService.handleUpdateBrand(
        orgId,
        data,
        async (updateData) => {
          return brandRepository.updateBrand(data.id, orgId, {
            name: updateData.name?.trim(),
            description: updateData.description?.trim() || null,
            industry: updateData.industry?.trim() || null,
            website_url: updateData.website_url?.trim() || null,
            ...(updateData.logo_url !== undefined ? { logo_url: updateData.logo_url } : {}),
            updated_at: new Date().toISOString(),
          });
        },
        supabaseBrandLogoStorage
      );
    },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: brandKeys.allMetrics(orgId) });
        queryClient.invalidateQueries({ queryKey: brandKeys.all(orgId) });
        queryClient.invalidateQueries({ queryKey: brandKeys.withActiveFilter(orgId) });
      }
      queryClient.invalidateQueries({ queryKey: ['all-brand-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['brands-with-active-filter'] });
      toast({
        title: 'Success',
        description: 'Brand updated successfully!',
      });
    },
    onError: (error: any) => {
      console.error('Error updating brand:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update brand. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
